import { useCallback } from "react";

import { skipToken } from "@reduxjs/toolkit/query";
import { useSelector } from "react-redux";

import { selectProfileSSOid } from "../../app-main/selectors/profile.selectors";
import { useSearchMessagesQuery } from "../../services/messages.service";
import { useGetSpaceReviewsBatchQuery } from "../../services/reviews.service";
import {
  useGetOwnerReviewsLastViewedQuery,
  useMarkSpaceReviewsViewedMutation,
} from "../../services/spaces.service";
import { toUnreadReviewSpaceCards } from "../services/api/unread-reviews/converter";
import {
  HOST_SPACES_MESSAGE_LOOKUP_SIZE,
  UNREAD_REVIEW_SPACES_BATCH_SIZE,
} from "../services/api/unread-reviews/converter.consts";
import type { UnreadReviewSpaceCardData } from "../types/model/unread-review";

export type UseUnreadSpaceReviewsResult = {
  cards: UnreadReviewSpaceCardData[];
  isError: boolean;
  isLoading: boolean;
  markSpaceReviewsViewed: (spaceId: string) => Promise<unknown>;
};

const useUnreadSpaceReviews = (): UseUnreadSpaceReviewsResult => {
  const ssoId = useSelector(selectProfileSSOid);

  const lastViewed = useGetOwnerReviewsLastViewedQuery(
    ssoId ? undefined : skipToken,
  );
  const lastViewedSpaces = lastViewed.data?.spaces;
  const ownerSpaceIds = (lastViewedSpaces ?? []).map((space) => space.space_id);

  const ownerQuerySettled = lastViewed.isSuccess || lastViewed.isError;
  const ownerReturnedNoSpaces = ownerQuerySettled && 0 === ownerSpaceIds.length;

  const messageThreads = useSearchMessagesQuery(
    ssoId && ownerReturnedNoSpaces
      ? { participant_ssoid: ssoId, size: HOST_SPACES_MESSAGE_LOOKUP_SIZE }
      : skipToken,
  );
  const messageSpaceIds = Array.from(
    new Set(
      (messageThreads.data?.threads ?? [])
        .map((thread) => thread.thread?.space_id)
        .filter((spaceId): spaceId is string => Boolean(spaceId)),
    ),
  );

  const candidateSpaceIds =
    ownerSpaceIds.length > 0 ? ownerSpaceIds : messageSpaceIds;
  const batchSpaceIds = candidateSpaceIds.slice(
    0,
    UNREAD_REVIEW_SPACES_BATCH_SIZE,
  );

  const spaceReviews = useGetSpaceReviewsBatchQuery(
    batchSpaceIds.length > 0 ? { spaceIds: batchSpaceIds } : skipToken,
  );

  const [markViewed] = useMarkSpaceReviewsViewedMutation();
  const markSpaceReviewsViewed = useCallback(
    (spaceId: string) => markViewed({ spaceId }).unwrap(),
    [markViewed],
  );

  return {
    cards: toUnreadReviewSpaceCards({
      batchEntries: spaceReviews.data,
      lastViewedSpaces: lastViewedSpaces ?? [],
    }),
    isError: spaceReviews.isError,
    isLoading: messageThreads.isLoading || spaceReviews.isLoading,
    markSpaceReviewsViewed,
  };
};

export default useUnreadSpaceReviews;
